import { createContext, useContext } from 'react';

/**
 * What the host app tells the queue about AI drafts changing elsewhere. The
 * events carry ids only and never say who acted — the AI writes and drops
 * drafts as often as an agent does — so each handler reads the draft, or the
 * list, rather than trusting the event to describe the change.
 */
export type AiDraftEventHandlers = {
  /** Written, revised, re-filed, rejected or approved. */
  onUpserted: (aiDraftId: string) => void;
  /** Discarded, or dropped once the article it became is searchable. */
  onDeleted: (aiDraftId: string) => void;
  /** Events sent while the connection was down never arrived. */
  onReconnected: () => void;
};

/** Starts delivering events to the handlers, and returns what stops it. */
export type SubscribeToAiDraftEvents = (
  handlers: AiDraftEventHandlers,
) => () => void;

const AiDraftEventsContext = createContext<
  SubscribeToAiDraftEvents | undefined
>(undefined);

/**
 * Supplied by the host app, which alone holds the web socket. Injected via the
 * `subscribeToAiDraftEvents` prop on the knowledge management entry.
 */
export const AiDraftEventsProvider = AiDraftEventsContext.Provider;

export function useSubscribeToAiDraftEvents():
  | SubscribeToAiDraftEvents
  | undefined {
  return useContext(AiDraftEventsContext);
}
